/**
 * Design B bundle calculator. Reads the bundle variants Liquid wrote into
 * the page (prices in fils) and derives every figure from them, so nothing
 * here can disagree with checkout.
 */
import { scene, revealAll, countTo } from '@theme/clinic-motion';

const aed = (fils) => fils / 100;
const fmt = (n) => Math.round(n).toLocaleString('en-US');

class ClinicCalculator extends HTMLElement {
  connectedCallback() {
    try {
      this.data = JSON.parse(this.querySelector('[data-calc]')?.textContent || '[]');
    } catch {
      this.data = [];
    }
    this.steps = [...this.querySelectorAll('[data-step]')];
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.steps.forEach((step, i) => {
      step.addEventListener('click', () => this.select(i), { signal });
      step.addEventListener(
        'keydown',
        (event) => {
          const last = this.steps.length - 1;
          let next = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = i === last ? 0 : i + 1;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = i === 0 ? last : i - 1;
          if (next === null) return;
          event.preventDefault();
          this.select(next);
          this.steps[next].focus();
        },
        { signal }
      );
    });

    const initial = Math.max(0, this.steps.findIndex((s) => s.getAttribute('aria-checked') === 'true'));
    this.select(initial, true);

    const input = this.querySelector('[data-anchor-input]');
    if (input) {
      input.addEventListener('input', () => this.anchor(), { signal });
      this.anchor();
    }

    this.dispose = scene(this, ({ scroller }) => revealAll(this, { scroller }));
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.dispose?.();
  }

  select(index, instant = false) {
    const bundle = this.data[index];
    if (!bundle) return;
    this.steps.forEach((step, i) => {
      const on = i === index;
      step.setAttribute('aria-checked', String(on));
      step.tabIndex = on ? 0 : -1;
    });

    const pairs = Math.max(1, bundle.pairs);
    const perPair = aed(bundle.standard) / pairs;
    const expressPerPair = (aed(bundle.express) - aed(bundle.standard)) / pairs;
    const saving = bundle.compareAt > bundle.standard ? aed(bundle.compareAt - bundle.standard) : 0;

    const per = this.querySelector('[data-per]');
    if (instant) per.textContent = fmt(perPair);
    else countTo(per, perPair, { format: fmt });

    this.querySelector('[data-total]').textContent = fmt(aed(bundle.standard));
    const savingRow = this.querySelector('[data-saving-row]');
    savingRow.hidden = saving <= 0;
    this.querySelector('[data-saving]').textContent = fmt(saving);

    const expressRow = this.querySelector('[data-express-row]');
    expressRow.hidden = !(expressPerPair > 0);
    this.querySelector('[data-express]').textContent = fmt(expressPerPair);

    const book = this.querySelector('[data-book]');
    book.href = `${bundle.url}?variant=${bundle.standardId}`;
    this.querySelector('[data-book-pairs]').textContent = String(pairs);
  }

  anchor() {
    const box = this.querySelector('[data-anchor]');
    const input = this.querySelector('[data-anchor-input]');
    if (!box || !input) return;
    // Plain money, no percentages (C57): what new costs, and what you keep.
    const price = aed(Number(box.dataset.anchorPrice) || 0);
    const paid = Math.max(0, Number(input.value) || 0);
    const save = paid - price;
    this.querySelector('[data-anchor-new]').textContent = fmt(paid);
    this.querySelector('[data-anchor-save]').textContent = fmt(Math.max(0, save));
    this.querySelector('[data-anchor-save-line]').hidden = save <= 0;
    this.querySelector('[data-anchor-less]').hidden = save > 0;
  }
}

if (!customElements.get('clinic-calculator')) customElements.define('clinic-calculator', ClinicCalculator);
